/**
 * EN↔FR Language Pair Definition
 *
 * buildProfile() generates directional profiles:
 *   enFr = FR native → learn EN (UI in French)
 *   frEn = EN native → learn FR (UI in English)
 */

import type { LanguagePairDefinition, LanguageProfile } from '../types'
import { buildProfile } from '../build-profile'

const PAIR: LanguagePairDefinition = {
  id: 'en-fr',
  languages: {
    en: {
      lang: 'en',
      langName: 'English',
      shortCode: 'EN',
      scriptSample: 'Hello',
      phonetic: {
        type: 'ipa',
        fields: [
          { key: 'phonetic_us', label: 'US' },
          { key: 'phonetic_uk', label: 'UK' },
        ],
      },
      tts: {
        voices: [
          { accent: 'us', lang: 'en-US' },
          { accent: 'uk', lang: 'en-GB' },
        ],
        detectScript: /^$/,
      },
      ui: {
        inputPlaceholder: 'Enter words, phrases, or questions...',
        headingText: 'What do you want to learn today?',
        intentChips: [
          { label: 'List', intent: 'BATCH_LIST', suffix: 'how to say in French' },
          { label: 'Compare', intent: 'CONFUSABLE_PAIRS', type: 'compare', separator: 'vs' },
        ],
      },
      promptConfig: {
        classifierHint: 'User may input in English.',
      },
    },
    fr: {
      lang: 'fr',
      langName: 'French',
      shortCode: 'FR',
      scriptSample: 'Bonjour',
      phonetic: {
        type: 'ipa',
        fields: [
          { key: 'phonetic_us', label: 'FR' },
        ],
      },
      tts: {
        voices: [
          { accent: 'fr', lang: 'fr-FR' },
        ],
        detectScript: /[a-zàâçéèêëîïôùûüÿœæ]/i,
      },
      ui: {
        inputPlaceholder: 'Entrez des mots, phrases ou questions...',
        headingText: "Que voulez-vous apprendre aujourd'hui ?",
        intentChips: [
          { label: 'Liste', intent: 'BATCH_LIST', suffix: "comment dire en anglais" },
          { label: 'Comparer', intent: 'CONFUSABLE_PAIRS', type: 'compare', separator: 'vs' },
        ],
      },
      promptConfig: {
        classifierHint: 'User may input in French.',
        languageHints: [
          'For nouns, always include the definite article (le/la/les/l\') in "front" to indicate gender.',
          'For adjectives, show agreement in the example sentence (masculine/feminine).',
          'For verbs, include the auxiliary (avoir/être) in past tense examples.',
          'Distinguish between tu (informal) and vous (formal) — tag register:informal or register:formal.',
        ].join('\n'),
      },
      cardMeta: {
        keys: ['gender', 'plural', 'auxiliary'],
        promptDescription: `"card_meta" (JSON object, required): French-specific metadata:
- "gender": "m" or "f". Only for nouns.
- "plural": plural form. Only for nouns where plural is irregular.
- "auxiliary": "avoir" or "être". Only for verbs (indicating passé composé auxiliary).`,
      },
    },
  },
}

export const enFrPair = PAIR
export const enFr: LanguageProfile = buildProfile(PAIR, 'en')
export const frEn: LanguageProfile = buildProfile(PAIR, 'fr')
